import { cloneDeep, isObject, map } from 'lodash';
import { apiAxios } from '/@/modules/core/app/helpers/AxiosHelper';
import { decamelize } from 'humps';
import { TaskPromise } from '/@/modules/core/app/models/TaskPromise';
import { GenericModel } from '/@/modules/core/app/models/GenericModel';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { commits, getters } from '@/modules/core/app/helpers/store';
import ApiService from '@/modules/core/api/services/ApiService';

export class BaseResource {
  /**
   * @type {object<string, Promise<*>>}
   */
  promises = {};

  async getData(url, model = GenericModel, params = {}, headers = {}) {
    const config = { params, headers };
    const key = `${url}:${model.name}:${JSON.stringify(config)}`;

    if (this.promises[key]?.pending) {
      return this.parsePayload(await this.promises[key], model, params);
    }

    this.promises[key] = new TaskPromise(this.getDataRequest(url, model, params, headers));
    const result = await this.promises[key];
    delete this.promises[key];

    return this.parsePayload(result, model, params);
  }

  async getDataWithBlocking(url, model = GenericModel, params = {}, headers = {}) {
    return this.withBlocking(url, async () => this.getData(url, model, params, headers));
  }

  /**
   * @param url
   * @param model
   * @param params
   * @param headers
   * @returns {*}
   */
  async getDataRequest(url, model, params = {}, headers = {}) {
    this.sanitizeRequestParams(params || {});

    return apiAxios.get(url, { params, headers });
  }

  parsePayload(payload, model, params) {
    if (payload === null) {
      return payload;
    }

    payload = cloneDeep(payload);

    if (isObject(payload)) {
      if (params?.page) {
        payload.data = this.mapModel(model, payload.data);
      } else {
        payload = this.mapModel(model, payload);
      }
    }

    return payload;
  }

  /**
   * @param url
   * @param data
   * @param config
   * @returns {*}
   */
  async postData(url, data, config = {}) {
    // data = this.sanitizeData(data);
    return apiAxios.post(url, data, config);
  }

  async postDataWithBlocking(url, data, config = {}) {
    return this.withBlocking(url, async () => this.postData(url, data, config));
  }

  /**
   * @param url
   * @param data
   * @returns {*}
   */
  async putData(url, data) {
    // data = this.sanitizeData(data);
    return apiAxios.put(url, data);
  }

  /**
   * @param url
   * @param config
   * @returns {*}
   */
  async deleteData(url, config = {}) {
    return apiAxios.delete(url, config);
  }

  /**
   * @param url
   * @param config
   * @returns {Promise<*>}
   */
  async getBlob(url, config = {}) {
    return apiAxios.get(url, {
      responseType: 'blob',
      ...config
    });
  }

  async get(url, config = {}) {
    return apiAxios.get(url, config);
  }

  /**
   * @param model
   * @param data
   * @returns {Array}
   * @private
   */
  mapModel(model, data) {
    if (data instanceof Array) {
      return map(data, (datum) => model.factory(datum));
    }
    return model.factory(data);
  }

  /**
   * @param params
   */
  sanitizeRequestParams(params) {
    Object.entries(params).forEach(([key, value]) => {
      if (key === 'with') {
        value = value.join(',');
      }
      if (key === 'sort') {
        value = decamelize(value);
      }
      params[key] = value;
    });
  }

  /**
   * @param data
   */
  sanitizeData(data) {
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof BaseEntityModel) {
        if (Object.values(value).every((val) => val === null || val === '')) {
          delete data[key];
        }
      }
    });
  }

  async withBlocking(url, callback) {
    try {
      commits.api.setAllowedRequestUrl(url);
      ApiService.cancelPendingGetRequests();
      return await callback();
    } finally {
      commits.api.setAllowedRequestUrl(undefined);
    }
  }
}
